import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useAuth } from "@/lib/auth";
import { CartAPI } from "@/lib/api";

// Konteks keranjang terpadu:
// - Tamu (belum login): keranjang lokal disimpan di localStorage.
// - Sudah login: keranjang persisten di backend (tabel carts/cart_items).
// Badge ikon keranjang membaca `count` dari sini agar selalu sesuai isi nyata.

const CartContext = createContext(null);
const LS_KEY = "kd_guest_cart";

function readGuest() {
  try { return JSON.parse(localStorage.getItem(LS_KEY)) || []; } catch { return []; }
}

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [guestItems, setGuestItems] = useState(readGuest);
  const [serverCount, setServerCount] = useState(0);

  // Persistkan keranjang tamu.
  useEffect(() => {
    localStorage.setItem(LS_KEY, JSON.stringify(guestItems));
  }, [guestItems]);

  // Sinkronkan jumlah item keranjang backend (untuk badge) saat login/berubah.
  const refreshServer = useCallback(async () => {
    if (!user) { setServerCount(0); return; }
    try {
      const data = await CartAPI.get();
      setServerCount((data.lines || []).reduce((a, l) => a + Number(l.quantity || 0), 0));
    } catch { /* abaikan */ }
  }, [user]);

  useEffect(() => { refreshServer(); }, [refreshServer]);

  // Tambah produk ke keranjang. Tamu → lokal, anggota → backend.
  const addItem = useCallback(async (product, qty = 1) => {
    if (user) {
      if (typeof product.id !== "number") throw new Error("DUMMY_PRODUCT");
      await CartAPI.addItem(product.id, qty);
      await refreshServer();
      return;
    }
    setGuestItems((prev) => {
      const found = prev.find((p) => p.id === product.id);
      if (found) return prev.map((p) => (p.id === product.id ? { ...p, quantity: p.quantity + qty } : p));
      return [...prev, {
        id: product.id,
        name: product.name,
        image: product.image,
        price: Number(product.price) || 0,
        memberPrice: Number(product.memberPrice) || 0,
        quantity: qty,
      }];
    });
  }, [user, refreshServer]);

  const setGuestQty = useCallback((id, qty) => {
    setGuestItems((prev) =>
      qty < 1 ? prev.filter((p) => p.id !== id) : prev.map((p) => (p.id === id ? { ...p, quantity: qty } : p))
    );
  }, []);

  const removeGuest = useCallback((id) => setGuestItems((prev) => prev.filter((p) => p.id !== id)), []);
  const clearGuest = useCallback(() => setGuestItems([]), []);

  const count = user ? serverCount : guestItems.reduce((a, i) => a + i.quantity, 0);

  const value = {
    guestItems, count, addItem, setGuestQty, removeGuest, clearGuest, refreshServer, serverCount,
  };
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

// Halaman keranjang: anggota yang sudah login memakai keranjang di ruang anggota,
// tamu memakai keranjang portal publik.
export const cartPathFor = (user) => (user ? "/anggota/keranjang" : "/keranjang");

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
